/**
 * RegimePanel - Regime classification with live data
 */

import { useEffect, useState } from 'react'
import type { CompanyData } from '../../types'
import { getRegime, type RegimeData } from '../../services/api'
import { PrivateCompanyBanner, PrivateCompanyNoDataMessage } from '../shared/PrivateCompanyBanner'

interface RegimePanelProps {
  company: CompanyData | null
  initialData?: RegimeData | null
}

interface DataFreshness {
  source: 'live' | 'cached' | 'loading' | 'error'
  timestamp?: string
  age?: string
}

const formatAge = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

const isDataFresh = (timestamp: string): boolean => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  return diffMs < 60000 // Within 1 minute
}

const regimeDetails = {
  growth: {
    label: 'Growth',
    color: 'text-term-success',
    bgColor: 'bg-term-success/20',
    description: 'Strong revenue and FCF growth trajectory',
  },
  stable: {
    label: 'Stable',
    color: 'text-term-info',
    bgColor: 'bg-term-info/20',
    description: 'Consistent performance with predictable cash flows',
  },
  transition: {
    label: 'Transition',
    color: 'text-term-warning',
    bgColor: 'bg-term-warning/20',
    description: 'Company in strategic transition phase',
  },
  distressed: {
    label: 'Distressed',
    color: 'text-term-error',
    bgColor: 'bg-term-error/20',
    description: 'Financial challenges requiring attention',
  },
}

export function RegimePanel({ company, initialData }: RegimePanelProps) {
  const [regimeData, setRegimeData] = useState<RegimeData | null>(initialData || null)
  const [freshness, setFreshness] = useState<DataFreshness>({
    source: initialData ? 'cached' : 'loading',
    timestamp: initialData ? new Date().toISOString() : undefined,
  })

  useEffect(() => {
    // Track if the effect is still active (for cleanup)
    let isActive = true

    if (!company?.ticker) {
      setRegimeData(null)
      setFreshness({ source: 'loading' })
      return
    }

    // Skip fetching for private companies - they don't have public regime data
    if (company.isPrivate) {
      setRegimeData(null)
      // Don't set to 'loading' for private companies - they won't load public data
      setFreshness({ source: 'cached' })
      return
    }

    // Reset state when company changes
    setRegimeData(null)
    setFreshness({ source: 'loading' })

    // Show cached/initial data immediately if available
    if (initialData) {
      setRegimeData(initialData)
      setFreshness({
        source: 'cached',
        timestamp: new Date().toISOString(),
        age: 'loading fresh data...',
      })
    }

    // Fetch live data
    const fetchData = async () => {
      try {
        const result = await getRegime(company.ticker)

        // Check if effect is still active before updating state
        if (!isActive) return

        if (result.data) {
          // Validate that the response has expected regime data properties
          // The API might return { error: '...', ticker: '...' } for missing data
          const data = result.data as RegimeData & { timestamp?: string; dataSource?: string; error?: string }

          if (data.error || (data.currentRegime === undefined && data.regime === undefined)) {
            // API returned an error or missing data structure
            console.warn('Regime API returned incomplete data:', data)
            setFreshness({ source: 'error' })
            return
          }

          setRegimeData(result.data)
          const timestamp = data.timestamp || new Date().toISOString()
          const isFresh = isDataFresh(timestamp)
          setFreshness({
            source: isFresh ? 'live' : 'cached',
            timestamp,
            age: formatAge(timestamp),
          })
        } else if (result.error) {
          console.error('Regime API error:', result.error)
          setFreshness({ source: 'error' })
        } else {
          // Neither data nor error - unexpected response
          console.warn('Regime API returned unexpected response:', result)
          setFreshness({ source: 'error' })
        }
      } catch (error) {
        console.error('Failed to fetch regime:', error)
        if (isActive) {
          setFreshness({ source: 'error' })
        }
      }
    }

    fetchData()

    // Cleanup function to prevent stale state updates
    return () => {
      isActive = false
    }
  }, [company?.ticker, company?.isPrivate, initialData])

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to view regime analysis</p>
      </div>
    )
  }

  // Handle private companies - show banner and no-data message
  const isPrivate = company.isPrivate || company.quoteType === 'PRIVATE'
  if (isPrivate) {
    return (
      <div className="space-y-6">
        <PrivateCompanyBanner companyName={company.name} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-term-text">
            📈 Regime Analysis - {company.ticker}
            <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">PRIVATE</span>
          </h2>
        </div>

        <PrivateCompanyNoDataMessage
          dataType="Regime Analysis"
          suggestion="Regime classification requires historical financial data. Request audited financials and management accounts via the Data Room."
        />

        {/* Regime Classification Explanation */}
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">📊 About Regime Classification</h3>
          <p className="text-term-textDim text-sm mb-4">
            Regime classification categorizes companies into four operational states based on financial performance patterns:
          </p>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(regimeDetails).map(([key, details]) => (
              <div key={key} className={`p-3 rounded ${details.bgColor}`}>
                <div className={`font-semibold ${details.color}`}>{details.label}</div>
                <div className="text-term-textDim text-xs mt-1">{details.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Data Requirements */}
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">📋 Required Data for Regime Analysis</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              'Revenue Growth (3-5 years)',
              'Free Cash Flow Trends',
              'Operating Margin History',
              'Debt Service Coverage',
              'Working Capital Trends',
              'EBITDA Margins',
              'Capital Expenditure History',
              'Customer Concentration Data',
            ].map((doc, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <span className="text-term-warning">○</span>
                <span className="text-term-textDim">{doc}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-term-textDim mt-4">
            💡 Once financial data is uploaded to the Data Room, regime classification can be performed.
          </p>
        </div>
      </div>
    )
  }

  if (!regimeData && freshness.source === 'loading') {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Loading regime analysis for {company.ticker}...</p>
      </div>
    )
  }

  if (!regimeData && freshness.source === 'error') {
    return (
      <div className="flex items-center justify-center h-full text-term-error">
        <p>Failed to load regime analysis for {company.ticker}</p>
      </div>
    )
  }

  if (!regimeData) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>No regime analysis available for {company.ticker}</p>
      </div>
    )
  }

  const currentRegime = regimeData.currentRegime || regimeData.regime || 'stable'
  const regime = regimeDetails[currentRegime as keyof typeof regimeDetails] || regimeDetails.stable

  // Check if this is an ETF or fund
  const isETF = regimeData.quoteType === 'ETF'
  const isMutualFund = regimeData.quoteType === 'MUTUALFUND'
  const isNonEquity = isETF || isMutualFund

  return (
    <div className="space-y-6">
      {/* ETF/Fund Warning Banner */}
      {isNonEquity && (
        <div className="bg-term-warning/20 border border-term-warning rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{isETF ? '📦' : '🏦'}</span>
            <div>
              <h3 className="text-term-warning font-bold text-lg">
                {isETF ? 'ETF - Limited Regime Analysis' : 'Mutual Fund - Limited Regime Analysis'}
              </h3>
              <p className="text-term-textDim text-sm">
                {isETF
                  ? 'ETFs don\'t have a traditional business "regime" since they are collections of securities. The analysis below shows a default classification. Consider analyzing individual holdings for meaningful regime data.'
                  : 'Mutual funds don\'t operate like individual companies. Regime analysis is limited for these investment vehicles.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with freshness indicator */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          📈 Regime Analysis - {company.ticker}
          {isETF && <span className="ml-2 px-2 py-0.5 bg-term-warning/30 text-term-warning text-xs rounded font-bold">ETF</span>}
          {isMutualFund && <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">FUND</span>}
        </h2>
        <FreshnessIndicator freshness={freshness} />
      </div>

      {/* Main Classification */}
      <div className="grid grid-cols-3 gap-4">
        <div className="term-box p-6 col-span-1">
          <div className="text-term-textDim text-xs mb-2">Current Regime</div>
          <div className={`text-3xl font-bold ${regime.color}`}>
            {regime.label}
          </div>
          <p className="text-sm text-term-textDim mt-2">
            {regimeData.regimeDescription || regime.description}
          </p>
        </div>

        <div className="term-box p-6">
          <div className="text-term-textDim text-xs mb-2">Classification Confidence</div>
          <div className="text-3xl font-bold text-term-text">
            {((regimeData.regimeConfidence || 0.75) * 100).toFixed(0)}%
          </div>
          <ProgressBar value={regimeData.regimeConfidence || 0.75} />
        </div>

        <div className="term-box p-6">
          <div className="text-term-textDim text-xs mb-2">Data Source</div>
          <div className={`text-2xl font-bold ${freshness.source === 'live' ? 'text-term-success' : 'text-term-warning'}`}>
            {freshness.source === 'live' ? 'Live' : 'Cached'}
          </div>
          <div className="text-xs text-term-textDim mt-2">
            {freshness.age || 'Updated recently'}
          </div>
        </div>
      </div>

      {/* Regime Probabilities */}
      <div className="term-box p-4">
        <h3 className="text-sm font-medium text-term-text mb-4">Regime Probabilities</h3>
        <div className="space-y-3">
          {Object.entries(regimeDetails).map(([key, details]) => {
            const probability = regimeData.regimeProbabilities?.[key as keyof typeof regimeData.regimeProbabilities] || 0
            return (
              <div key={key} className="flex items-center gap-4">
                <span className={`w-24 text-sm ${details.color}`}>{details.label}</span>
                <div className="flex-1 term-progress">
                  <div
                    className={`term-progress-bar ${details.bgColor.replace('/20', '')}`}
                    style={{ width: `${probability * 100}%` }}
                  />
                </div>
                <span className="text-sm text-term-textDim w-12 text-right">
                  {(probability * 100).toFixed(0)}%
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Historical Regimes */}
      {regimeData.historicalRegimes && regimeData.historicalRegimes.length > 0 && (
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">Regime History</h3>
          <div className="flex items-center gap-1">
            {regimeData.historicalRegimes.slice(-8).map((h, i) => {
              const r = regimeDetails[h.regime as keyof typeof regimeDetails] || regimeDetails.stable
              return (
                <div
                  key={i}
                  className={`w-10 h-10 rounded ${r.bgColor} flex items-center justify-center`}
                  title={`${h.year}: ${r.label}`}
                >
                  <span className="text-xs text-term-textDim">{h.year}</span>
                </div>
              )
            })}
          </div>
          <div className="text-xs text-term-textDim mt-2">Historical regime classifications</div>
        </div>
      )}

      {/* Risk Factors */}
      {regimeData.riskFactors && regimeData.riskFactors.length > 0 && (
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">Key Risk Factors</h3>
          <div className="grid grid-cols-2 gap-2">
            {regimeData.riskFactors.map((factor, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-term-warning">⚠</span>
                <span className="text-sm text-term-text">{factor}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model Info */}
      <div className="term-box p-4">
        <h3 className="text-sm font-medium text-term-text mb-2">Analysis Details</h3>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-term-textDim">Sector:</span>{' '}
            <span className="text-term-text">{company.sector}</span>
          </div>
          <div>
            <span className="text-term-textDim">Industry:</span>{' '}
            <span className="text-term-text">{company.industry}</span>
          </div>
          <div>
            <span className="text-term-textDim">Method:</span>{' '}
            <span className="text-term-text">Dual-Model Verification</span>
          </div>
          <div>
            <span className="text-term-textDim">Source:</span>{' '}
            <span className={freshness.source === 'live' ? 'text-term-success' : 'text-term-warning'}>
              {freshness.source === 'live' ? 'SEC Filings + Yahoo Finance' : 'Cached Analysis'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface FreshnessIndicatorProps {
  freshness: DataFreshness
}

function FreshnessIndicator({ freshness }: FreshnessIndicatorProps) {
  const config = {
    live: { label: 'LIVE', color: 'text-term-success', bg: 'bg-term-success/20', icon: '●' },
    cached: { label: 'CACHED', color: 'text-term-warning', bg: 'bg-term-warning/20', icon: '○' },
    loading: { label: 'LOADING', color: 'text-term-info', bg: 'bg-term-info/20', icon: '◌' },
    error: { label: 'ERROR', color: 'text-term-error', bg: 'bg-term-error/20', icon: '✕' },
  }

  const { label, color, bg, icon } = config[freshness.source]

  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded ${bg}`}>
      <span className={color}>{icon}</span>
      <span className={`text-xs font-medium ${color}`}>{label}</span>
      {freshness.age && (
        <span className="text-xs text-term-textDim">({freshness.age})</span>
      )}
    </div>
  )
}

interface ProgressBarProps {
  value: number
  variant?: 'success' | 'info' | 'warning' | 'error'
}

function ProgressBar({ value, variant = 'success' }: ProgressBarProps) {
  const colors = {
    success: 'bg-term-success',
    info: 'bg-term-info',
    warning: 'bg-term-warning',
    error: 'bg-term-error',
  }

  return (
    <div className="term-progress mt-2">
      <div
        className={`term-progress-bar ${colors[variant]}`}
        style={{ width: `${value * 100}%` }}
      />
    </div>
  )
}
